import React, { useEffect, useState } from "react";
import { PaymentForm, CreditCard } from "react-square-web-payments-sdk";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, CreditCard as CardIcon, ShieldCheck } from "lucide-react";

const BRAND_LABEL = {
  VISA: "Visa", MASTERCARD: "Mastercard", AMERICAN_EXPRESS: "Amex",
  DISCOVER: "Discover", JCB: "JCB", UNIONPAY: "UnionPay", CHINA_UNIONPAY: "UnionPay",
  DINERS_CLUB: "Diners", OTHER_BRAND: "Card",
};

export default function SavedCards({ embedded = false }) {
  const [cards, setCards] = useState(null);
  const [methods, setMethods] = useState(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/payments/cards/me");
      setCards(r.data || []);
    } catch (e) {
      setCards([]);
    }
  };

  useEffect(() => {
    api.get("/payments/methods").then(r => setMethods(r.data)).catch(() => setMethods({}));
    load();
  }, []);

  const handleSquareToken = async (token, verifiedBuyer) => {
    setBusy(true);
    try {
      await api.post("/payments/cards/save", {
        source_id: token.token,
        verification_token: verifiedBuyer?.token,
      });
      toast.success("Card saved");
      setAdding(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save card");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (saved_card_id) => {
    if (!window.confirm("Remove this card?")) return;
    try {
      await api.delete(`/payments/cards/${saved_card_id}`);
      toast.success("Card removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove card");
    }
  };

  const cardEnabled = methods?.card?.enabled;

  return (
    <div className={embedded ? "" : "max-w-2xl"}>
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="overline mb-1">Payment methods</div>
            <h2 className="font-serif text-3xl text-[#F5F2EB]">Saved cards</h2>
          </div>
          {!adding && (
            <button
              data-testid="add-card-btn"
              onClick={() => setAdding(true)}
              className="btn-gold"
            >
              <Plus size={14} className="inline mr-2"/>Add a card
            </button>
          )}
        </div>
      )}

      {/* Existing cards */}
      {cards === null ? (
        <div className="text-sm text-[#A1A1AA]">Loading…</div>
      ) : cards.length === 0 && !adding ? (
        <div className="border border-dashed border-white/10 p-8 text-center">
          <CardIcon className="mx-auto text-[#A1A1AA] mb-3" size={32}/>
          <p className="text-sm text-[#A1A1AA] mb-4">No cards saved yet.</p>
          {embedded && (
            <button
              data-testid="add-card-btn-embedded"
              onClick={() => setAdding(true)}
              className="btn-ghost"
            ><Plus size={14} className="inline mr-2"/>Add a card</button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {cards.map(c => (
            <div
              key={c.saved_card_id}
              data-testid={`saved-card-${c.saved_card_id}`}
              className="border border-white/10 bg-[#0A0A0A] p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-8 bg-[#D4AF37] text-[#0a0a0a] flex items-center justify-center text-[10px] font-bold tracking-wider">
                  {BRAND_LABEL[c.brand] || "CARD"}
                </div>
                <div>
                  <div className="text-[#F5F2EB] text-sm tracking-[0.18em]">•••• •••• •••• {c.last4}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mt-1">
                    {c.cardholder_name || "On file"} · expires {String(c.exp_month).padStart(2, "0")}/{String(c.exp_year).slice(-2)}
                  </div>
                </div>
              </div>
              <button
                data-testid={`delete-card-${c.saved_card_id}`}
                onClick={() => remove(c.saved_card_id)}
                className="text-[#71717A] hover:text-red-400 transition-colors p-2"
                aria-label="Remove card"
              >
                <Trash2 size={16}/>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add card form */}
      {adding && cardEnabled && (
        <div className="mt-6 border border-white/10 bg-[#0A0A0A] p-5">
          <div className="flex items-center gap-2 text-xs text-[#A1A1AA] mb-4">
            <ShieldCheck size={14} className="text-[#D4AF37]"/>
            Secured by Square · card details never touch our servers
          </div>
          <PaymentForm
            applicationId={methods.card.app_id}
            locationId={methods.card.location_id}
            cardTokenizeResponseReceived={handleSquareToken}
            createVerificationDetails={() => ({
              amount: "0.00",
              currencyCode: "USD",
              intent: "STORE",
              billingContact: {},
            })}
          >
            <CreditCard
              buttonProps={{
                css: {
                  backgroundColor: "#D4AF37",
                  color: "#050505",
                  fontFamily: "Outfit, sans-serif",
                  fontWeight: 600,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  fontSize: "12px",
                  "&:hover": { backgroundColor: "#C29B2E" },
                }
              }}
            >
              {busy ? "Saving…" : "Save card"}
            </CreditCard>
          </PaymentForm>
          <button
            onClick={() => setAdding(false)}
            className="mt-3 text-xs uppercase tracking-[0.2em] text-[#71717A] hover:text-[#F5F2EB]"
            data-testid="cancel-add-card"
          >
            Cancel
          </button>
        </div>
      )}
      {adding && !cardEnabled && (
        <div className="mt-6 border border-white/10 p-5 text-sm text-[#A1A1AA]">
          Card storage isn&apos;t configured. Please contact support.
        </div>
      )}
    </div>
  );
}
